// Copyright (c) Docugami, Inc. All rights reserved.

import { useReportOperationByMetadataStatus } from "hooks/reportOperationStatus";
import { ReportEntry } from "networking/reportsApi";
import { OperationMetadata } from "store/reports/reportsSlice";
import { CellStyles, CellContentContainer, LoadingCellContent } from "./ReportGridCellStyles";
import { ReportTableCell } from "./ReportTableCell";
import { getClassNames } from "components/cards/CardStyles";
import { AutomationProps } from "utils/Automation";
import { useCallback } from "react";
import { RowResizeableCell } from "./ReportGridRowResizableCell";

interface GridCellProps extends AutomationProps {
    entry: ReportEntry;
    folderId: string;
    fileName: string;
    styles?: CellStyles;
    isLoading?: boolean;
    disabled?: boolean;
    isGrouped?: boolean;
    onClick: (folderId: string, instanceId?: string) => void;
    onResize: (newHeight: number) => void;
}

export const GridCell = (props: GridCellProps): JSX.Element => {
    const metadataPredicate = useCallback((metadata: OperationMetadata) =>
        metadata.folderId === props.folderId || metadata.entryId === props.entry.id,
        [props.folderId, props.entry.id])

    const isBeingOperatedOn = useReportOperationByMetadataStatus(metadataPredicate);

    return (
        <RowResizeableCell onResize={props.onResize}>
            <CellContentContainer
                data-automation-id={props.automationId}
                key={`${props.folderId}.${props.entry.id}`}
                className={getClassNames(props.styles?.root)}
                disabled={!!props.disabled || isBeingOperatedOn}>
                {
                    !!props.isLoading ?
                        <LoadingCellContent /> :
                        <ReportTableCell
                            entry={props.entry}
                            folderId={props.folderId}
                            fileName={props.fileName}
                            onClick={!props.disabled ? (instanceId) => props.onClick(props.folderId, instanceId) : undefined}
                            disabled={props.disabled} />
                }
            </CellContentContainer>
        </RowResizeableCell>
    )
}
